'use client'

import Link from 'next/link'
import { BlogPost } from '@prisma/client'
import { formatDate } from '@/lib/utils'

interface BlogCardProps {
  post: BlogPost
}

export default function BlogCard({ post }: BlogCardProps) {
  return (
    <article className="card group cursor-pointer">
      <Link href={`/blog/${post.slug}`}>
        {post.featuredImageUrl && (
          <div className="mb-4 rounded-lg overflow-hidden">
            <img
              src={post.featuredImageUrl}
              alt={post.featuredImageAlt || post.title}
              className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-200"
            />
          </div>
        )}
        
        <div className="space-y-3">
          {post.category && (
            <Link
              href={`/blog/category/${encodeURIComponent(post.category)}`}
              className="inline-block px-3 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full hover:bg-primary/20 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {post.category}
            </Link>
          )}
          
          <h3 className="text-xl font-bold text-slate-900 group-hover:text-primary transition-colors">
            {post.title}
          </h3>
          
          {post.excerpt && (
            <p className="text-slate-600 line-clamp-3">
              {post.excerpt}
            </p>
          )}
          
          <div className="flex items-center text-sm text-slate-500 space-x-4">
            <time dateTime={post.publishedAt?.toISOString()}>
              {post.publishedAt && formatDate(post.publishedAt)}
            </time>
            {post.readingTime && (
              <span>{post.readingTime} min read</span>
            )}
          </div>
          
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {post.tags.slice(0, 3).map((tag) => (
                <Link
                  key={tag}
                  href={`/blog/tag/${encodeURIComponent(tag)}`}
                  className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded hover:bg-slate-200 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  #{tag}
                </Link>
              ))}
            </div>
          )}
        </div>
      </Link>
    </article>
  )
}
